import React, { useState } from 'react';
import { LandingPage } from '../LandingPage';
import type { AuthProvider } from '../../utils/authProvider';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

interface AuthViewProps {
  onLogin: (payload: { identifier: string; password: string }) => Promise<void>;
  onRegister: (payload: { fullName: string; phone: string; email?: string; password: string }) => Promise<void>;
  authProvider?: AuthProvider;
  loading: boolean;
  error: string | null;
  deferredPrompt?: BeforeInstallPromptEvent | null;
  isInstallPromptAvailable?: boolean;
  onInstallClick?: () => Promise<void>;
  apkUrl?: string;
}

type AuthMode = 'login' | 'register' | 'forgot' | 'landing';

const AuthView: React.FC<AuthViewProps> = ({
  onLogin,
  onRegister,
  authProvider = 'php',
  loading,
  error,
  deferredPrompt = null,
  isInstallPromptAvailable = false,
  onInstallClick = async () => { },
  apkUrl,
}) => {
  const [mode, setMode] = useState<AuthMode>('landing');

  const [loginIdentifier, setLoginIdentifier] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  // Etats pour mot de passe oublie
  const [resetStep, setResetStep] = useState<'request' | 'confirm' | 'success'>('request');
  const [resetIdentifier, setResetIdentifier] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetNewPasswordConfirm, setResetNewPasswordConfirm] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (mode === 'login') {
      await onLogin({ identifier: loginIdentifier, password: loginPassword });
    } else if (mode === 'register') {
      await onRegister({
        fullName,
        phone,
        email: email.trim() ? email.trim() : undefined,
        password: registerPassword,
      });
    } else {
      // mode 'forgot' gere via handleForgotSubmit
    }
  };

  const switchTo = (target: AuthMode) => {
    setMode(target);
    setLoginIdentifier('');
    setLoginPassword('');
    setRegisterPassword('');
    setResetStep('request');
    setResetIdentifier('');
    setResetToken('');
    setResetNewPassword('');
    setResetNewPasswordConfirm('');
    setResetError(null);
  };

  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetError(null);
    try {
      if (resetStep === 'request') {
        if (!resetIdentifier.trim()) {
          setResetError('Veuillez saisir votre telephone ou e-mail.');
          return;
        }
        setResetLoading(true);
        const response = await fetch('/fedapay_integration/request_password_reset.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ identifier: resetIdentifier.trim() }),
        });
        const raw = await response.text();
        let data: any = null;
        try { data = raw ? JSON.parse(raw) : null; } catch { }
        if (!response.ok || data?.status !== 'ok') {
          throw new Error(data?.message || 'Impossible de traiter la demande.');
        }
        setResetStep('confirm');
      } else if (resetStep === 'confirm') {
        if (!resetToken.trim()) {
          setResetError('Veuillez saisir le code de reinitialisation reçu.');
          return;
        }
        if (resetNewPassword.length < 6) {
          setResetError('Le nouveau mot de passe doit contenir au moins 6 caracteres.');
          return;
        }
        if (resetNewPassword !== resetNewPasswordConfirm) {
          setResetError('Les mots de passe ne correspondent pas.');
          return;
        }
        setResetLoading(true);
        const response = await fetch('/fedapay_integration/confirm_password_reset.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: resetToken.trim(), new_password: resetNewPassword }),
        });
        const raw = await response.text();
        let data: any = null;
        try { data = raw ? JSON.parse(raw) : null; } catch { }
        if (!response.ok || data?.status !== 'ok') {
          throw new Error(data?.message || 'Echec de reinitialisation.');
        }
        setResetStep('success');
        // Retour automatique a la connexion apres 2 secondes
        setTimeout(() => switchTo('login'), 2000);
      }
    } catch (err: any) {
      setResetError(err?.message || 'Une erreur est survenue.');
    } finally {
      setResetLoading(false);
    }
  };

  // Show landing page first
  if (mode === 'landing') {
    return (
      <LandingPage
        onGetStarted={() => setMode('register')}
        onLogin={() => setMode('login')}
        deferredPrompt={deferredPrompt}
        isInstallPromptAvailable={isInstallPromptAvailable}
        onInstallClick={onInstallClick}
        apkUrl={apkUrl}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-light via-white to-brand-light flex items-center justify-center px-4 py-10 md:py-14">
      <div className="relative w-full max-w-4xl">
        {/* Back to landing button */}
        <button
          onClick={() => setMode('landing')}
          className="mb-4 flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          <span className="text-sm font-medium">Retour à l'accueil</span>
        </button>

        <div className="absolute -top-10 -left-10 h-32 w-32 rounded-full bg-brand-accent/20 blur-3xl" />
        <div className="absolute -bottom-12 -right-12 h-36 w-36 rounded-full bg-brand-secondary/20 blur-3xl" />
        <div className="relative overflow-hidden rounded-3xl border border-white/60 bg-white/90 shadow-2xl backdrop-blur-lg">
          <div className="grid gap-0 md:grid-cols-2">
            <div className="hidden md:flex flex-col justify-center bg-gradient-to-br from-brand-accent via-brand-secondary to-brand-accent text-white p-10">
              {/* Logo M animé amélioré */}
              <div className="flex justify-center mb-8">
                <div className="relative">
                  {/* Anneau extérieur animé */}
                  <div
                    className="absolute -inset-3 rounded-3xl opacity-75"
                    style={{
                      background: 'linear-gradient(45deg, #fbbf24, #f59e0b, #fbbf24, #f59e0b)',
                      backgroundSize: '300% 300%',
                      animation: 'gradientRotate 3s ease infinite, spin 8s linear infinite',
                    }}
                  />
                  {/* Logo principal */}
                  <div
                    className="relative w-28 h-28 rounded-2xl flex items-center justify-center shadow-2xl"
                    style={{
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.1) 100%)',
                      backdropFilter: 'blur(10px)',
                      animation: 'float 3s ease-in-out infinite, scaleBreath 4s ease-in-out infinite',
                    }}
                  >
                    <span
                      className="text-6xl font-black text-white"
                      style={{
                        textShadow: '0 0 25px rgba(251, 191, 36, 0.8), 0 4px 15px rgba(251, 191, 36, 0.5)',
                      }}
                    >
                      M
                    </span>
                  </div>
                  {/* Particules lumineuses */}
                  <div
                    className="absolute -top-2 -right-2 w-4 h-4 rounded-full bg-amber-400"
                    style={{ animation: 'sparkle 2s ease-in-out infinite' }}
                  />
                  <div
                    className="absolute -bottom-1 -left-1 w-3 h-3 rounded-full bg-amber-300"
                    style={{ animation: 'sparkle 2s ease-in-out infinite 0.5s' }}
                  />
                  <div
                    className="absolute top-1/2 -right-3 w-2 h-2 rounded-full bg-yellow-300"
                    style={{ animation: 'sparkle 2s ease-in-out infinite 1s' }}
                  />
                </div>
              </div>
              <style>{`
                @keyframes float {
                  0%, 100% { transform: translateY(0px); }
                  50% { transform: translateY(-12px); }
                }
                @keyframes scaleBreath {
                  0%, 100% { transform: scale(1); }
                  50% { transform: scale(1.05); }
                }
                @keyframes shimmer {
                  0%, 100% { background-position: 0% 50%; }
                  50% { background-position: 100% 50%; }
                }
                @keyframes gradientRotate {
                  0%, 100% { background-position: 0% 50%; }
                  50% { background-position: 100% 50%; }
                }
                @keyframes spin {
                  from { transform: rotate(0deg); }
                  to { transform: rotate(360deg); }
                }
                @keyframes sparkle {
                  0%, 100% { opacity: 0.4; transform: scale(1); }
                  50% { opacity: 1; transform: scale(1.3); }
                }
              `}</style>
              <div className="mb-6">
                <span className="inline-flex items-center px-3 py-1 rounded-full bg-white/20 text-sm tracking-wide uppercase">
                  Carnet de Mesures
                </span>
              </div>
              <h2 className="text-3xl font-bold mb-4">Votre atelier, partout avec vous</h2>
              <p className="text-white/80 leading-relaxed">
                Gerez vos clientes, vos mesures et vos paiements en toute simplicite. Commencez gratuitement avec
                trois clientes, puis debloquez toutes les fonctionnalites quand vous etes prete.
              </p>
              <div className="mt-10 flex items-center space-x-3 text-sm text-white/70">
                <span className="h-px flex-1 bg-white/30" />
                <span>{mode === 'login' ? 'Nouveau dans Carnet de Mesures ?' : 'Deja inscrit ?'}</span>
                <span className="h-px flex-1 bg-white/30" />
              </div>
              <button
                onClick={() => switchTo(mode === 'login' ? 'register' : 'login')}
                className="mt-4 inline-flex items-center justify-center px-4 py-2 rounded-full bg-white text-brand-dark font-semibold shadow-sm hover:shadow-md transition-all"
              >
                {mode === 'login' ? 'Creer un compte' : 'Se connecter'}
              </button>
            </div>
            <div className="p-6 sm:p-8 md:p-12">
              <div className="mb-6 rounded-3xl bg-gradient-to-br from-brand-accent via-brand-secondary to-brand-accent p-5 text-white shadow md:hidden">
                {/* Logo M miniaturisé sur mobile */}
                <div className="flex items-center gap-4 mb-3">
                  <div className="relative flex-shrink-0">
                    <div
                      className="absolute -inset-1.5 rounded-xl opacity-75"
                      style={{
                        background: 'linear-gradient(45deg, #fbbf24, #f59e0b, #fbbf24)',
                        backgroundSize: '300% 300%',
                        animation: 'gradientRotate 3s ease infinite',
                      }}
                    />
                    <div
                      className="relative w-12 h-12 rounded-lg flex items-center justify-center shadow-lg"
                      style={{
                        background: 'rgba(255,255,255,0.2)',
                        backdropFilter: 'blur(8px)',
                        animation: 'float 3s ease-in-out infinite',
                      }}
                    >
                      <span
                        className="text-2xl font-black text-white"
                        style={{
                          textShadow: '0 0 15px rgba(251, 191, 36, 0.8)',
                        }}
                      >
                        M
                      </span>
                    </div>
                    <div
                      className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-amber-400"
                      style={{ animation: 'sparkle 2s ease-in-out infinite' }}
                    />
                  </div>
                  <div>
                    <span className="inline-flex items-center rounded-full bg-white/20 px-2 py-0.5 text-xs uppercase tracking-wide">
                      Carnet de Mesures
                    </span>
                  </div>
                </div>
                <h2 className="text-xl font-bold">Votre atelier, partout avec vous</h2>
                <p className="mt-2 text-sm text-white/80 leading-relaxed">
                  Retrouvez vos clients, vos mesures et vos paiements sur n'importe quel ecran, meme en deplacement.
                </p>
              </div>

              <div className="mb-6 flex w-full rounded-full bg-brand-light/60 p-1 md:hidden">
                <button
                  onClick={() => switchTo('login')}
                  className={`flex-1 py-2 rounded-full text-sm font-medium transition ${mode === 'login' ? 'bg-white shadow text-brand-dark' : 'text-gray-500'
                    }`}
                >
                  Connexion
                </button>
                <button
                  onClick={() => switchTo('register')}
                  className={`flex-1 py-2 rounded-full text-sm font-medium transition ${mode === 'register' ? 'bg-white shadow text-brand-dark' : 'text-gray-500'
                    }`}
                >
                  Inscription
                </button>
              </div>

              {authProvider === 'firebase' ? (
                <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  Mode Firebase actif: connexion et inscription pretes, synchronisation backend encore en transition.
                </div>
              ) : null}

              <h1 className="text-3xl font-bold text-brand-dark mb-2">
                {mode === 'login' ? 'Bon retour !' : 'Bienvenue dans Carnet de Mesures'}
              </h1>
              <p className="text-gray-500 mb-8">
                {mode === 'login'
                  ? 'Connectez-vous pour retrouver vos clients et vos projets.'
                  : 'Creez votre espace en quelques secondes et commencez a enregistrer vos clients.'}
              </p>

              {error && (
                <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                {mode === 'register' && (
                  <>
                    <div>
                      <label htmlFor="fullName" className="block text-sm font-medium text-gray-600 mb-1">
                        Nom complet
                      </label>
                      <input
                        id="fullName"
                        name="fullName"
                        type="text"
                        required
                        placeholder="Ex. Awa Couture"
                        value={fullName}
                        onChange={(event) => setFullName(event.target.value)}
                        className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm focus:border-brand-accent focus:outline-none focus:ring-2 focus:ring-brand-accent/40 transition"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-600 mb-1">
                        Numero de telephone
                      </label>
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        placeholder="+229 90 00 00 00"
                        value={phone}
                        onChange={(event) => setPhone(event.target.value)}
                        className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm focus:border-brand-accent focus:outline-none focus:ring-2 focus:ring-brand-accent/40 transition"
                      />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label htmlFor="email" className="block text-sm font-medium text-gray-600">
                          Adresse e-mail <span className="text-gray-400">(optionnel)</span>
                        </label>
                      </div>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="atelier@example.com"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm focus:border-brand-accent focus:outline-none focus:ring-2 focus:ring-brand-accent/40 transition"
                      />
                      <p className="mt-2 text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg flex items-start gap-2">
                        <span className="text-base">💡</span>
                        <span>Conseil : Ajoutez un email pour pouvoir réinitialiser votre mot de passe en cas d'oubli.</span>
                      </p>
                    </div>
                  </>
                )}

                {mode === 'login' && (
                  <div>
                    <label htmlFor="identifier" className="block text-sm font-medium text-gray-600 mb-1">
                      Telephone ou e-mail
                    </label>
                    <input
                      id="identifier"
                      name="identifier"
                      type="text"
                      required
                      autoComplete="username"
                      placeholder="Ex. +22990000000"
                      value={loginIdentifier}
                      onChange={(event) => setLoginIdentifier(event.target.value)}
                      className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm focus:border-brand-accent focus:outline-none focus:ring-2 focus:ring-brand-accent/40 transition"
                    />
                    <div className="mt-2 text-right">
                      <button
                        type="button"
                        onClick={() => { setResetIdentifier(loginIdentifier); setMode('forgot'); }}
                        className="text-xs text-brand-accent hover:underline"
                      >
                        Mot de passe oublie ?
                      </button>
                    </div>
                  </div>
                )}

                {mode !== 'forgot' && (
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-600 mb-1">
                      Mot de passe
                    </label>
                    <input
                      id="password"
                      name="password"
                      type="password"
                      required
                      minLength={6}
                      autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                      placeholder="Au moins 6 caracteres"
                      value={mode === 'login' ? loginPassword : registerPassword}
                      onChange={(event) =>
                        mode === 'login'
                          ? setLoginPassword(event.target.value)
                          : setRegisterPassword(event.target.value)
                      }
                      className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm focus:border-brand-accent focus:outline-none focus:ring-2 focus:ring-brand-accent/40 transition"
                    />
                  </div>
                )}

                {mode === 'forgot' && (
                  <div className="space-y-4">
                    {resetStep === 'request' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-600 mb-1">Telephone ou e-mail</label>
                          <input
                            type="text"
                            value={resetIdentifier}
                            onChange={(e) => setResetIdentifier(e.target.value)}
                            placeholder="Ex. +22990000000"
                            className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm focus:border-brand-accent focus:outline-none focus:ring-2 focus:ring-brand-accent/40 transition"
                          />
                        </div>
                        {resetError && <p className="text-sm text-red-600">{resetError}</p>}
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={handleForgotSubmit}
                            disabled={resetLoading}
                            className="rounded-xl bg-brand-accent px-4 py-2 text-sm font-semibold text-white shadow hover:bg-brand-secondary disabled:opacity-70"
                          >
                            {resetLoading ? 'Envoi...' : 'Envoyer le code'}
                          </button>
                          <button
                            type="button"
                            onClick={() => switchTo('login')}
                            className="text-sm text-gray-500 hover:underline"
                          >
                            Retour a la connexion
                          </button>
                        </div>
                      </>
                    )}
                    {resetStep === 'confirm' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-600 mb-1">Code de reinitialisation</label>
                          <input
                            type="text"
                            value={resetToken}
                            onChange={(e) => setResetToken(e.target.value)}
                            placeholder="Collez le code reçu par email ici"
                            className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm focus:border-brand-accent focus:outline-none focus:ring-2 focus:ring-brand-accent/40 transition"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-600 mb-1">Nouveau mot de passe</label>
                          <input
                            type="password"
                            value={resetNewPassword}
                            onChange={(e) => setResetNewPassword(e.target.value)}
                            minLength={6}
                            placeholder="Au moins 6 caracteres"
                            className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm focus:border-brand-accent focus:outline-none focus:ring-2 focus:ring-brand-accent/40 transition"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-600 mb-1">Confirmer le mot de passe</label>
                          <input
                            type="password"
                            value={resetNewPasswordConfirm}
                            onChange={(e) => setResetNewPasswordConfirm(e.target.value)}
                            minLength={6}
                            className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm focus:border-brand-accent focus:outline-none focus:ring-2 focus:ring-brand-accent/40 transition"
                          />
                        </div>
                        {resetError && <p className="text-sm text-red-600">{resetError}</p>}
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={handleForgotSubmit}
                            disabled={resetLoading}
                            className="rounded-xl bg-brand-accent px-4 py-2 text-sm font-semibold text-white shadow hover:bg-brand-secondary disabled:opacity-70"
                          >
                            {resetLoading ? 'Validation...' : 'Valider'}
                          </button>
                          <button
                            type="button"
                            onClick={() => switchTo('login')}
                            className="text-sm text-gray-500 hover:underline"
                          >
                            Retour a la connexion
                          </button>
                        </div>
                      </>
                    )}
                    {resetStep === 'success' && (
                      <div className="space-y-3">
                        <p className="text-sm text-green-700">Mot de passe reinitialise. Redirection vers la connexion...</p>
                        <button
                          type="button"
                          onClick={() => switchTo('login')}
                          className="rounded-xl bg-brand-accent px-4 py-2 text-sm font-semibold text-white shadow hover:bg-brand-secondary"
                        >
                          Aller a la connexion maintenant
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {mode !== 'forgot' && (
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full rounded-xl bg-brand-accent px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-brand-accent/30 transition hover:bg-brand-secondary disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {loading ? 'Veuillez patienter...' : mode === 'login' ? 'Se connecter' : 'Creer mon espace gratuit'}
                  </button>
                )}

                <p className="text-xs text-gray-400 text-center">
                  En continuant, vous acceptez nos conditions d'utilisation. Vous pourrez gerer votre abonnement a tout
                  moment depuis l'application.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthView;
